import { createClient } from '@supabase/supabase-js';

/**
 * SECURITY ARCHITECTURE NOTE:
 *
 * 1. SUPABASE_URL & SUPABASE_KEY (Anon):
 *    These are intended for client-side use. In a production build, these should be
 *    populated via environment variables (e.g., import.meta.env.VITE_SUPABASE_URL).
 *    Security is enforced via Row Level Security (RLS) on the Supabase Dashboard.
 *
 * 2. SERVICE_ROLE_KEY:
 *    NEVER include the service_role key in this file or any client-side code.
 *    It bypasses RLS and grants full database access.
 */

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_KEY = import.meta.env.VITE_SUPABASE_KEY;

if (!SUPABASE_URL || !SUPABASE_KEY) {
  const missing = [
    !SUPABASE_URL ? 'VITE_SUPABASE_URL' : null,
    !SUPABASE_KEY ? 'VITE_SUPABASE_KEY' : null
  ].filter(Boolean);

  throw new Error(
    `[Config] Missing required env var(s): ${missing.join(', ')}. Configure them in client/.env or your deployment environment.`
  );
}

const getLegacySupabaseStorageKey = () => {
  try {
    const host = new URL(SUPABASE_URL).hostname;
    const projectRef = host.split('.')[0];
    return projectRef ? `sb-${projectRef}-auth-token` : null;
  } catch (_err) {
    return null;
  }
};

const resolveSessionStorage = () => {
  if (typeof window === 'undefined') return null;

  try {
    const probeKey = '__k2_session_storage_probe__';
    window.sessionStorage.setItem(probeKey, '1');
    window.sessionStorage.removeItem(probeKey);
    return window.sessionStorage;
  } catch (_err) {
    return null;
  }
};

const clearLegacyLocalStorageToken = () => {
  if (typeof window === 'undefined') return;

  const legacyKey = getLegacySupabaseStorageKey();
  if (!legacyKey) return;

  try {
    window.localStorage.removeItem(legacyKey);
  } catch (_err) {
    // ignore storage errors
  }
};

const sessionStorage = resolveSessionStorage();
clearLegacyLocalStorageToken();

export const supabase = createClient(SUPABASE_URL, SUPABASE_KEY, {
  auth: {
    storageKey: 'k2_supabase_auth',
    persistSession: Boolean(sessionStorage),
    storage: sessionStorage ?? undefined
  }
});
